import { useLocalSearchParams } from "expo-router"
import { Show, useObservable } from "@legendapp/state/react"
import { SafeAreaView } from "react-native-safe-area-context"
import MCIcon from "@react-native-vector-icons/material-design-icons"
import { ActivityIndicator, View, Pressable, Linking } from "react-native"

import { ar } from "@src/i18n"
import { sharedStyles, trpc } from "@src/utils"
import { Header, List, Text } from "@src/components"
import { Alert } from "@src/components/ui/confirm"
import { CustomerAddressForm, CustomerAddressFormModalState } from "@src/components/customer-address-form"

export default function Addresses() {
  const params = useLocalSearchParams<{ id: string }>()
  const customerId = Number.parseInt(params.id, 10)
  const customer = trpc.customers.get.useQuery({ id: customerId })
  const addresses = trpc.addresses.list.useQuery({ customerId })
  const deleteAddress = trpc.addresses.delete.useMutation()
  const utils = trpc.useUtils()

  const modalState$ = useObservable<CustomerAddressFormModalState>(null)

  const handleDelete = (addressId: number) => {
    Alert.alert(ar.deleteAddress, ar.confirmDeleteAddress, [
      { text: ar.cancel, style: "cancel" },
      {
        text: ar.delete,
        style: "destructive",
        onPress: () => {
          deleteAddress.mutate(
            { id: addressId },
            {
              onSuccess: () => {
                utils.addresses.list.refetch()
              },
            },
          )
        },
      },
    ])
  }

  if (customer.isLoading || addresses.isLoading)
    return (
      <>
        <Header title={ar.addresses} />
        <ActivityIndicator className="mt-4" />
      </>
    )

  const listHeader = (
    <View className="bg-white p-4 mb-3 rounded-lg">
      <Text className="text-lg font-bold mb-3">{ar.customerDetails}</Text>
      <View className="space-y-2">
        <View className="flex-row gap-2">
          <Text className="text-sm text-gray-500">{ar.name}:</Text>
          <Text className="text-sm font-medium flex-1">{customer.data?.name}</Text>
        </View>
        {customer.data?.phoneNumbers[0] && (
          <View className="flex-row gap-2">
            <Text className="text-sm text-gray-500">{ar.phone}:</Text>
            <Text className="text-sm font-medium flex-1">{customer.data?.phoneNumbers[0].phone}</Text>
          </View>
        )}
      </View>
    </View>
  )

  return (
    <>
      <Header
        title={ar.addresses}
        right={
          <Pressable onPress={() => modalState$.set({ type: "add" })} hitSlop={20} className="h-full justify-center px-2 active:opacity-70">
            <MCIcon name="plus" size={24} color="#2563eb" />
          </Pressable>
        }
      />
      <List
        contentContainerStyle={sharedStyles.contentContainer}
        ListHeaderComponent={listHeader}
        keyExtractor={(item) => item.id.toString()}
        data={addresses.data ?? []}
        renderItem={({ item }) => {
          return (
            <View className="bg-white p-4 mb-2 rounded-lg">
              <View className="flex-row justify-between items-start mb-2">
                <Text className="text-base font-bold flex-1">{item.label}</Text>
                <View className="flex-row gap-2">
                  {item.lat && item.lng ? (
                    <Pressable
                      onPress={() => Linking.openURL(`https://maps.google.com/?q=${item.lat},${item.lng}`)}
                      className="p-2 bg-green-50 rounded active:bg-green-100"
                    >
                      <MCIcon name="map-marker" size={18} color="#16a34a" />
                    </Pressable>
                  ) : null}
                  <Pressable onPress={() => modalState$.set({ type: "edit", addressId: item.id })} className="p-2 bg-blue-50 rounded active:bg-blue-100">
                    <MCIcon name="pencil" size={18} color="#2563eb" />
                  </Pressable>
                  <Pressable onPress={() => handleDelete(item.id)} className="p-2 bg-red-50 rounded active:bg-red-100">
                    <MCIcon name="delete" size={18} color="#dc2626" />
                  </Pressable>
                </View>
              </View>
              <Text className="text-base font-semibold mb-2">{item.area.name}</Text>

              <View className="space-y-1">
                {item.building && (
                  <Text className="text-sm text-gray-600">
                    {ar.building}: {item.building}
                  </Text>
                )}
                {item.floor && (
                  <Text className="text-sm text-gray-600">
                    {ar.floor}: {item.floor}
                  </Text>
                )}
                {item.details && (
                  <Text className="text-sm text-gray-600">
                    {ar.addressDetails}: {item.details}
                  </Text>
                )}
              </View>
            </View>
          )
        }}
        ListEmptyComponent={
          <View className="items-center justify-center p-8">
            <Text className="text-gray-500 text-center">{ar.noAddresses}</Text>
          </View>
        }
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
      />

      <Show if={modalState$}>
        {() => {
          const state = modalState$.get()
          if (!state) return null

          return (
            <CustomerAddressForm
              customerId={customerId}
              addressId={state.type === "edit" ? state.addressId : undefined}
              onClose={() => modalState$.set(null)}
            />
          )
        }}
      </Show>
    </>
  )
}
